import { Analytics } from '@util/Analytics';
import { BasicLoginDetails } from '@util/Auth';
import { translate } from '@util/Localization';
import { Form } from 'antd';
import React, { FunctionComponent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { capitalise, isDefined } from 'ts-utils';
import { Color } from '../../../config/Color';
import { ActionType } from '../../../store/ActionTypes';
import { AppSelector, useAppDispatch, useAppStore } from '../../../store/Store';
import { RegistrationNewUserDetails } from '../../../store/user/effects/RegisterUser';
import { appOverride } from '../../../util/AppUtils';
import * as icons from '../../assets/images/Icons';
import { ForgotPasswordForm, LoginForm, SignUpForm } from '../../SignUpForm';
import { AppCheckbox } from '../../styles/Checkbox';
import { FormContainer } from '../../styles/Containers';
import { CopyContainer } from '../../styles/CopyContainer';
import { NextButton, SecondaryButton } from '../../styles/LayoutButtons';
import { HighlightLink } from '../../styles/Link';
import { BodySM, BodyXXS, Heading1 } from '../../styles/Typography';

export type AuthLayoutConfig = Pick<
  RegisterFormProps,
  'registerButtonTitle' | 'registerPrivacyDeclaration'
> &
  Pick<RegisterContainerProps, 'privacyLogos'> &
  Pick<LoginContainerProps, 'loginButtonTitle' | 'loginTitle'> & {
    registerTitle: string;
    registerBody: string[];
    defaultsToLogin?: boolean;
  };

type LoginContainerProps = {
  loginTitle: string;
  loginButtonTitle: string;
  onRegister: () => void;
  onForgotPassword: (email: string) => void;
};

export const LoginContainer: FunctionComponent<LoginContainerProps> = (
  props: LoginContainerProps
) => {
  const { loading } = useAppStore(AppSelector.userState);
  const dispatch = useAppDispatch();
  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);
  const [form] = Form.useForm();

  function handleSubmit(values: BasicLoginDetails) {
    const { email, password } = values;
    dispatch({ type: ActionType.LOGIN_USER, details: { email, password } });
  }

  return (
    <>
      <LeftCopyContainer>
        <div>
          <Heading>{props.loginTitle}</Heading>
        </div>
      </LeftCopyContainer>
      <Form
        form={form}
        onFinish={handleSubmit}
        onValuesChange={(_changedValues, values) => {
          const fields = values as BasicLoginDetails | undefined;
          setSubmitButtonEnabled(
            fields !== undefined && fields?.email?.length > 0 && fields?.password?.length > 0
          );
        }}
      >
        <AuthFormContainer>
          <LoginForm
            onForgotPassword={() =>
              props.onForgotPassword((form.getFieldValue('email') as string | undefined) ?? '')
            }
          />
        </AuthFormContainer>
        <div style={{ margin: '20px auto' }}>
          <NextButton loading={loading} disabled={!submitButtonEnabled}>
            {props.loginButtonTitle}
          </NextButton>
        </div>
      </Form>
      <AuthFooter
        title={'Don’t have an account?'}
        onClick={props.onRegister}
        buttonTitle={'Sign up'}
      ></AuthFooter>
    </>
  );
};

type ForgotPasswordContainerProps = {
  onBackToLogin: () => void;
  initialEmail: string;
};

export const ForgotPasswordContainer: FunctionComponent<ForgotPasswordContainerProps> = ({
  onBackToLogin,
  initialEmail,
}) => {
  const { loading, error } = useAppStore(AppSelector.userState);
  const dispatch = useAppDispatch();
  const [emailSent, setEmailSent] = useState(false);
  const emailSendSuccessful = emailSent && !loading && !error;

  useEffect(() => {
    if (error) {
      setEmailSent(false);
    }
  }, [error]);

  function handleSubmit(values: { email: string }) {
    dispatch({ type: ActionType.RESET_PASSWORD, email: values.email });
    setEmailSent(true);
  }

  return (
    <>
      {emailSendSuccessful ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <BodySM style={{ marginBottom: '20px' }}>
            A password reset link has been sent. You should receive it in your inbox shortly.
          </BodySM>
          <NextButton onClick={onBackToLogin}>Back to Login</NextButton>
        </div>
      ) : (
        <>
          <LeftCopyContainer>
            <div>
              <Heading>Reset Password</Heading>
              <BodySM>Enter your email to receive a password reset link.</BodySM>
            </div>
          </LeftCopyContainer>
          <Form onFinish={handleSubmit} initialValues={{ email: initialEmail }}>
            <AuthFormContainer>
              <ForgotPasswordForm />
            </AuthFormContainer>
            <div style={{ margin: '20px auto' }}>
              <NextButton loading={loading}>Send Reset Link</NextButton>
            </div>
          </Form>
        </>
      )}

      {!emailSendSuccessful && (
        <>
          <div style={{ textAlign: 'center', marginBottom: '20px', padding: '0 20px' }}>
            <BodyXXS>
              Something went wrong. Please try again or contact support at{' '}
              <HighlightLink href={`mailto:${translate('links.supportEmail')}`}>
                {translate('links.supportEmail')}
              </HighlightLink>
              .
            </BodyXXS>
          </div>
          <AuthFooter
            title={'Remember your password?'}
            onClick={onBackToLogin}
            buttonTitle={'Log in'}
          />
        </>
      )}
    </>
  );
};

type RegisterContainerProps = RegisterFormProps & {
  registerTitle: string;
  registerBody: string[];
  /** Compliance badges rendered at the very bottom of the sign-up view. */
  privacyLogos?: Array<{ src: string; altText: string }>;
  onLogin: () => void;
};

export const RegisterContainer: FunctionComponent<RegisterContainerProps> = (
  props: RegisterContainerProps
) => {
  return (
    <>
      <LeftCopyContainer>
        <div>
          <Heading1>{props.registerTitle}</Heading1>
          {props.registerBody.map((text, i) => (
            <BodySM key={i}>{text}</BodySM>
          ))}
        </div>
      </LeftCopyContainer>
      <RegisterForm
        registerPrivacyDeclaration={props.registerPrivacyDeclaration}
        firstName={props.firstName}
        registerButtonTitle={props.registerButtonTitle}
      />
      <AuthFooter
        title={'Already have an account?'}
        onClick={props.onLogin}
        buttonTitle={'Log in'}
      ></AuthFooter>
      {isDefined(props.privacyLogos) && <PrivacyLogos logos={props.privacyLogos} />}
    </>
  );
};

const PrivacyLogos: FunctionComponent<{ logos: Array<{ src: string; altText: string }> }> = ({
  logos,
}) => (
  <PrivacyLogosRow>
    {logos.map((logo) => (
      <PrivacyLogo key={logo.src} src={logo.src} alt={logo.altText} />
    ))}
  </PrivacyLogosRow>
);

type RegisterFormProps = {
  registerButtonTitle: string;
  registerPrivacyDeclaration: string[];
  firstName: string | undefined;
};

const RegisterForm: FunctionComponent<RegisterFormProps> = (props) => {
  const { loading } = useAppStore(AppSelector.userState);
  const dispatch = useAppDispatch();
  const [accept, setAccept] = useState(false);

  function handleSubmit(values: RegistrationNewUserDetails) {
    const { name, lastName, email, password, confirmEmail } = values;

    dispatch({
      type: ActionType.REGISTER_USER,
      details: {
        email,
        password,
        confirmEmail,
        name: capitalise(name),
        lastName: capitalise(lastName) ?? '',
      },
    });
  }

  return (
    <Form onFinish={handleSubmit} initialValues={{ name: props.firstName }}>
      <RegisterFormContainer>
        <SignUpForm
          callout={
            <Callout>
              <CalloutIcon src={icons.lightbulb} alt="" />
              <BodyXXS style={{ margin: 0, textAlign: 'left' }}>
                {`Remember this password. You’ll use it to log in to the ${translate(
                  'product'
                )} app.`}
              </BodyXXS>
            </Callout>
          }
        />
      </RegisterFormContainer>
      <BodyXXS style={{ textAlign: 'left', padding: `0px ${AUTH_INSET}` }} data-test="authText">
        {props.registerPrivacyDeclaration}
      </BodyXXS>
      <div style={{ padding: `8px ${AUTH_INSET}`, display: 'flex' }}>
        <AcceptCheckbox
          id="auth-accept"
          checked={accept}
          onChange={() => {
            setAccept(!accept);
          }}
        />
        <label htmlFor="auth-accept">
          <BodyXXS>
            I accept the{' '}
            <HighlightLink
              onClick={() => Analytics.track({ event: Analytics.Event.o_terms_opened })}
              href={translate('links.terms')}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms
            </HighlightLink>{' '}
            &amp;{' '}
            <HighlightLink
              onClick={() => Analytics.track({ event: Analytics.Event.o_privacy_opened })}
              href={translate('links.privacy')}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </HighlightLink>
          </BodyXXS>
        </label>
      </div>
      <div style={{ margin: '20px auto' }}>
        <NextButton loading={loading} disabled={!accept}>
          {props.registerButtonTitle}
        </NextButton>
      </div>
    </Form>
  );
};

type AuthFooterProps = {
  onClick: () => void;
  title: string;
  buttonTitle: string;
};

const AuthFooter: FunctionComponent<AuthFooterProps> = (props) => (
  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 40 }}>
    <SecondaryButton onClick={props.onClick}>
      {props.title} {props.buttonTitle}
    </SecondaryButton>
  </div>
);

// Nerva draws the form without a card, so its fields, copy and checkbox line up with the button
// instead of sitting inside the card padding the other products need.
const AUTH_INSET = appOverride({ nerva: '0px', default: '16px' });

const AuthFormContainer = styled(FormContainer)`
  padding-left: ${AUTH_INSET};
  padding-right: ${AUTH_INSET};
`;

// The sign-up view ends with the callout rather than a field, so it doesn't need the standard
// container spacing before the privacy declaration that follows.
const RegisterFormContainer = styled(AuthFormContainer)`
  padding-bottom: 0;
  margin-bottom: ${({ theme }) => theme.spacing._8px};
`;

// Rendered inside the sign-up form, directly below the password field, so it picks up the same
// vertical rhythm as the fields above it rather than an InfoBox's standalone margins.
const Callout = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._12px};
  margin: 0;
  padding: ${({ theme }) => theme.spacing._16px};
  border-radius: ${appOverride({ nerva: '24px', default: '8px' })};
  background-color: ${Color.surface.muted};
`;

const CalloutIcon = styled.img`
  flex-shrink: 0;
  width: ${({ theme }) => theme.spacing._24px};
  height: ${({ theme }) => theme.spacing._24px};
`;

const PrivacyLogosRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing._20px};
  margin-bottom: ${({ theme }) => theme.spacing._32px};
`;

const PrivacyLogo = styled.img`
  height: 56px;
  width: auto;
`;

const AcceptCheckbox = styled(AppCheckbox)`
  margin-right: ${({ theme }) => theme.spacing._20px};
  .ant-checkbox-checked .ant-checkbox-inner {
    border: 0;
  }
`;

const LeftCopyContainer = styled(CopyContainer)`
  text-align: left;
  padding: ${({ theme }) => theme.spacing._16px} ${AUTH_INSET} 0;
  margin: 0px;
`;

const Heading = styled(Heading1)`
  margin: ${({ theme }) => `${theme.spacing._32px} 0 ${theme.spacing._16px} 0`};
  line-height: 42px;
`;

export const PageContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing._32px};
`;
